import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { clearCart } = useCart();
    const [state, setState] = useState({ status: "verifying", orderId: null });
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "failed", orderId: null });
            return;
        }
        let attempts = 0;
        let timer;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    if (!cleared.current) {
                        cleared.current = true;
                        clearCart();
                    }
                    setState({ status: "paid", orderId: data.order_id });
                    return;
                }
            } catch {
                /* keep polling */
            }
            attempts += 1;
            if (attempts < 20) {
                timer = setTimeout(poll, 2000);
            } else {
                setState({ status: "timeout", orderId: null });
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId, clearCart]);

    return (
        <div data-testid="payment-success-page" className="flex min-h-screen items-center justify-center bg-vault-black px-6 text-stone-900">
            <div className="w-full max-w-md border border-vault-border p-10 text-center">
                {state.status === "verifying" && (
                    <>
                        <Loader2 size={44} className="mx-auto animate-spin text-vault-red" />
                        <h1 className="mt-6 font-display text-3xl uppercase">Verifying Payment</h1>
                        <p className="mt-4 font-mono text-xs tracking-[0.2em] text-stone-500">TALKING TO STRIPE...</p>
                    </>
                )}
                {state.status === "paid" && (
                    <>
                        <CheckCircle2 size={44} className="mx-auto text-vault-red" />
                        <h1 className="mt-6 font-display text-3xl uppercase">Payment Confirmed</h1>
                        <p data-testid="paid-order-id" className="mt-3 font-mono text-sm tracking-[0.2em] text-vault-red">
                            {state.orderId}
                        </p>
                        <p className="mt-6 text-sm leading-relaxed text-stone-600">
                            Your pair goes under the spray can next. We'll email you when it ships.
                        </p>
                    </>
                )}
                {(state.status === "failed" || state.status === "timeout") && (
                    <>
                        <XCircle size={44} className="mx-auto text-vault-red" />
                        <h1 className="mt-6 font-display text-3xl uppercase">
                            {state.status === "timeout" ? "Still Processing" : "Something Went Wrong"}
                        </h1>
                        <p className="mt-6 text-sm leading-relaxed text-stone-600">
                            If your card was charged, your order is safe — check your email or contact the studio.
                        </p>
                    </>
                )}
                <Link
                    data-testid="back-to-shop-link"
                    to="/"
                    className="btn-sweep mt-10 inline-block border border-vault-red px-8 py-3 font-mono text-xs tracking-[0.25em] text-stone-900"
                >
                    BACK TO THE VAULT
                </Link>
            </div>
        </div>
    );
}
